import { ApiResponse, AttributeTypeValue, ParamDefRow, ParamDefRowNoId, paramDefRowSchema, paramDefRowSchemaNoId, ParamDefRowView, ParamTypeValue, User } from "@bonusmap/api-types";
import { NunjucksEnvCallbacks, templateExistsInMemory, templateParse, templateParseToElement } from "../../logic/templateParse.js";
import { StepsComponent } from "../base-components/steps-component.js";
import { showConfirm, showError } from "../../modals.js";
import { FormComponent } from "../form-component/form-component.js";
import { ParameterTypeComponent } from "../parameter-type/parameter-type-component.js";
import { paramAttributeCheckValidity, paramAttributeReportValidity } from "../../utils/paramAttributeCheck.js";
import { PARAM_TYPE_MAP, PARAM_TYPE_VALUE_MAP } from "../../utils/lists.js";
import { AttributesComponent } from "../attributes-input/attributes-component.js";
import { isNumber } from "@bonusmap/shared_ui/library";
import { createChoicesInstance } from "../../utils/createChoicesInstance.js";

const { Choices } = window;
type ParameterComponentSteps = Record<"view" | "edit" | "manage", string>;
export class ParameterComponent extends StepsComponent {
    static webComponentName: string = "parameters-input";
    static observedAttributes = ['data-parameter'];
    protected _templateId: string = "form-parameters-input";
    protected _step: string = 'parameters-view';
    public readonly steps: ParameterComponentSteps = {
        "view": "parameters-view",
        "edit": "parameters-list",
        "manage": "parameters-form"
    };
    protected panelList!: HTMLDivElement;
    protected parametersView!: HTMLElement;
    protected addParameterBtn!: HTMLButtonElement;
    protected editParameterBtn!: HTMLButtonElement;
    protected removeParameterBtn!: HTMLButtonElement;
    protected listSelect!: HTMLSelectElement;
    // private parametersForm!: FormComponent;
    private choicesInstance!: InstanceType<Window['Choices']>;
    private _value: string = "";
    protected parametersCache: Record<number, ParamDefRowView> = {};
    protected _nunjucksEnvCallbacks!: NunjucksEnvCallbacks;

    protected get parametersForm(): FormComponent {
        return this.roleElementSelector(this.steps.manage) as FormComponent;
    }
    get nunjucksEnvCallbacks(): NunjucksEnvCallbacks {
        if (!this._nunjucksEnvCallbacks) {
            this._nunjucksEnvCallbacks = {
                globals: {},
                filters: {
                    "typeDisplayName": (typeValue: string): string => {
                        return this.getTypeDisplayName(typeValue);
                    },
                    "typeValueDisplayName": (typeValue: string): string => {
                        return this.getTypeValueDisplayName(typeValue);
                    }
                }
            };
        }
        return this._nunjucksEnvCallbacks;
    }
    get readOnly(): boolean {
        return super.readOnly;
    }

    get value(): string {
        return this._value;
    }

    private set value(newValue: string) {
        this._value = newValue;
        this.valueChanged(newValue);
        this.setAttribute('value', newValue.toString());
    }
    private isValidValue(value: string): boolean {
        if (!value || !isNumber(value) || this.loadError) {
            return false; // Invalid value
        }
        return true;
    }
    get current(): ParamDefRowView | null {
        if (!this.value || !isNumber(this.value) || this.loadError) {
            return null; // No valid value set
        }
        const cached = this.getCached(this.value);
        if (cached) {
            return cached; // Return from cache if available
        }
        return null;
    }


    get compact(): boolean {
        return this.hasAttribute('compact');
    }
    get minimal(): boolean {
        return this.attributeBoolOption('minimal', false);
    }
    get noDelete(): boolean {
        return this.attributeBoolOption('noDelete', false);
    }
    get noEdit(): boolean {
        return this.attributeBoolOption('noEdit', false);
    }


    constructor() {
        super();
        this.globalEventListeners();
    }
    public putInEditMode(dispatchEvent = false): void {
        this.goToStep(this.steps.edit, this, dispatchEvent);
    }
    public putInViewMode(dispatchEvent = false): void {
        this.goToStep(this.steps.view, this, dispatchEvent);
    }

    // nome della classe globale da aggiungere al body quando sono in manage mode
    protected manageModeGlobalClassName = 'parameters-manage-mode';

    private globalEventListeners(): void {
        this.addEventListener('value-changed', (event: Event) => {
            this.renderProcessView(); // Render the current attribute view
        });
        this.addEventListener('step-changed', (event: Event) => {
            const detail = (event as CustomEvent).detail;
            const { step, target } = detail;
            document.body.classList.toggle(this.manageModeGlobalClassName, step === this.steps.manage);
        })
        this.addEventListener('before-step-changed', (event: Event) => {
            const detail = (event as CustomEvent).detail;
            const { step, target } = detail;
            const user = this.user;
            let data: any = {}
            if (step === this.steps.manage && this.parametersForm) {
                this.parametersForm.innerHTML = ''; // Clear the form before switching steps
                if (target === this.addParameterBtn) {
                    this.parametersForm.setAttribute('data-action', 'add');
                }
                else if (target === this.editParameterBtn) {
                    this.parametersForm.setAttribute('data-action', 'edit');
                    if (this.current) {
                        data = this.current; // Use current parameter data for editing
                    }
                    else {
                        console.warn('Nessun parametro corrente trovato per la modifica');
                        return; // No current parameter to edit
                    }
                }
                data = {
                    ...data,
                    isAdmin: this.isAdmin,
                    isSuperAdmin: this.isSuperAdmin,
                    isEdit: this.isEdit,
                    noDelete: this.noDelete,
                    noEdit: this.noEdit,
                    loggedOrganizationId: this.loggedOrganizationId,
                    loggedOrganizationName: this.loggedOrganizationName,
                    organization_id: isNumber(data.organization_id) ? data.organization_id : user.organization_id || 0,
                }
                this.parametersForm.innerHTML =
                    templateParse(`form-parameters-form`, data, this.nunjucksEnvCallbacks) || "";
                if (data.id) {
                    this.parametersForm.setAttribute('data-id', data.id.toString());
                }
                else {
                    this.parametersForm.removeAttribute('data-id');
                }
                // Re-scansiona i form controls dopo il cambiamento del DOM
                this.parametersForm.refreshFormControls();
            }
        });
    }

    private processParameterValue(): void {
        const attributeValue = this.getAttribute('value');
        const componentLoaded = () => {
            this.dispatch('loaded', { origin: this }, true, true); // Dispatch loaded event
        }
        if (!attributeValue) {
            console.warn('Nessun ID attributo specificato, non posso processare il valore');
            componentLoaded();
            return;
        }
        this.addParameterToChoicesById(attributeValue)
            .then(() => {
                this.value = attributeValue; // Set the value after adding to choices
            })
            .catch((error) => {
                console.error('Errore durante l\'aggiunta del parametro a Choices.js:', error);
            })
            .finally(() => {
                componentLoaded();
            });
    }

    private getCached(id: number | string): ParamDefRowView | null {
        if (!id || !isNumber(id as string)) {
            console.warn('ID attributo non specificato, non posso cercare nella cache');
            return null;
        }
        const numId = parseInt(id as string, 10);
        return this.parametersCache[numId] || null;
    }
    private loadError: boolean = false;
    private async getParameterById(id: number | string): Promise<ParamDefRowView | null> {
        try {
            this.loadError = false;
            if (!id || !isNumber(id as string)) {
                return null;
            }
            const response = await window.api.manager.params.getByIdView(parseInt(id as string, 10));
            if (response.success) {
                return response.data as ParamDefRowView;
            } else {
                console.error('Error fetching attribute by ID:', response.error);
                // qui devi mettere in errore il componente
                this.loadError = true; // non esiste il parametro
                return null;
            }
        } catch (error) {
            console.error('Error in getAttributeById:', error);
            this.loadError = true;
            return null;
        }
    }



    private async addParameterToChoicesById(id: number | string): Promise<boolean> {
        const parameter = await this.getParameterById(id);
        if (!parameter) {
            return false;
        }

        // Aggiungo il parametro alla cache
        this.parametersCache[parameter.id] = parameter;

        const choice = {
            value: parameter.id.toString(),
            label: parameter.name,
            selected: true,
            customProperties: {
                description: parameter.description || '',
                type_name: parameter.type_name,
                type_value: parameter.type_value,
                organization_id: parameter.organization_id || 0,
                organization_name: parameter.organization_name || '',
                is_core: parameter.is_core || parameter.organization_id === 0,
                attribute_id: parameter.attribute_id || null,
                attribute_name: parameter.attribute_name || '',
                is_linked_to_attribute: !!parameter.attribute_id,
            }
        };

        this.choicesInstance?.setChoices([choice], 'value', 'label', false);
        return true;
    }

    private async performSearch(searchTerm: string): Promise<void> {
        if (!searchTerm || searchTerm.length == 0) {
            this.choicesInstance?.clearChoices();
            // return; <- commentata per permettere ricerca senza termine
        }
        try {
            let whereClause = searchTerm && searchTerm.length > 0
                ? `name ILIKE $1 OR description ILIKE $2`
                : `1=1`; // Cerca tutti gli elementi se non c'è termine di ricerca
            let paramDefSchema = searchTerm && searchTerm.length > 0
                ? [`${searchTerm}%`, `%${searchTerm}%`]
                : [];
            const orderBy = 'name ASC';
            const limit = 20; // Limit results to 20 for performance
            const result = await window.api.manager
                .params.query(whereClause, paramDefSchema, orderBy, limit);
            if (result.data && result.data.length > 0) {
                // Aggiungo tutti i nuovi oggetti alla cache
                result.data.forEach((item: ParamDefRowView) => {
                    this.parametersCache[item.id] = item;
                });

                const choices = result.data.map((item: ParamDefRowView) => {
                    return {
                        value: item.id.toString(),
                        label: item.name,
                        customProperties: {
                            description: item.description || '',
                            type_value: item.type_value,
                            organization_id: item.organization_id || 0,
                            organization_name: item.organization_name || '',
                            is_core: item.is_core || item.organization_id === 0,
                            attribute_id: item.attribute_id || null,
                            attribute_name: item.attribute_name || '',
                            is_linked_to_attribute: !!item.attribute_id,
                        }
                    };
                });
                this.choicesInstance?.setChoices(choices, 'value', 'label', true);
            }
            else {
                this.choicesInstance?.clearChoices();
            }
        } catch (error) {
            console.error('Error during search:', error);
            this.choicesInstance?.clearChoices();
            return;

        }
    }
    private get hideCloseButton(): boolean {
        return this.attributeBoolOption('hide-close-button', false);
    }
    // posso avere un template personalizzato per la modalità vista
    private get viewTemplateName(): string {
        const attributeTemplate = this.attributeStringOption('view-template', '');
        if (attributeTemplate && templateExistsInMemory(attributeTemplate)) {
            return attributeTemplate;
        }
        let viewTemplate = `form-parameters-input-view`;
        if (this.minimal) {
            viewTemplate += '-minimal';
        }
        else {
            if (this.compact) {
                viewTemplate += '-compact';
            }
        }
        return viewTemplate;
    }
    private renderProcessView(): void {
        const parameter = this.current;
        if (!this.parametersView) {
            this.parametersView = this.roleElementSelector('parameters-view') as HTMLElement;
        }
        if (!this.parametersView) {
            console.warn('Elemento parameters-view non trovato, impossibile renderizzare la vista');
            return;
        }
        this.parametersView.innerHTML = ''; // Clear previous content

        let data: Record<string, any> = {
            readOnly: this.readOnly,
            compact: this.compact
        };
        if (parameter) {
            data = {
                ...data,
                ...parameter,
                steps: this.steps,
                is_linked_to_attribute: !!parameter.attribute_id
            }
        }
        templateParseToElement(
            this.viewTemplateName,
            this.parametersView,
            data, false, this.nunjucksEnvCallbacks);
    }
    private initializeChoices(): void {
        if (!this.listSelect) {
            return;
        }
        this.choicesInstance = createChoicesInstance(this.listSelect, {
            searchEnabled: true,
            removeItemButton: true,
            loadingText: 'Ricerca in corso...',
            noResultsText: 'Nessun parametro trovato',
            noChoicesText: 'Digita almeno un carattere per cercare',
            searchPlaceholderValue: 'Digita per cercare parametri...',
            maxItemCount: 20,
            // Personalizzazione template items nei risultati di ricerca
            callbackOnCreateTemplates: (template) => {
                return {
                    choice: (classNames, choice, selectText, groupName) => {
                        return this.createCustomChoiceTemplate(template, classNames, choice, selectText, groupName);
                    }
                };
            }
        });
        this.listSelect.addEventListener('search', (event: any) => {
            const searchTerm = event.detail.value;
            // if (searchTerm && searchTerm.length >= 1) {
            // consente l'avvio anche se il termine è vuoto
            this.performSearch(searchTerm);
            // }
        });
        this.listSelect.addEventListener('addItem', (event: any) => {
            const id = event.detail.value;
            const cached = this.getCached(id);
            if (cached) {
                this.value = cached.id.toString(); // Set the value to the ID of the added attribute
            } else {
                // Fallback: se non è in cache, impostiamo comunque il value e proviamo a recuperarlo
                console.warn('Parameter not found in cache, setting value and trying to fetch:', id);
                this.value = id;
                // Proviamo a recuperare il parametro e aggiungerlo alla cache
                this.getParameterById(id).then(param => {
                    if (param) {
                        this.parametersCache[param.id] = param;
                        this.renderProcessView(); // Re-render with new data
                    }
                });
            }
        });
        this.listSelect.addEventListener('removeItem', (event: any) => {
            this.value = ''; // Clear the value when an item is removed
        });

        // Carica i dati iniziali al focus se il dropdown è vuoto
        this.listSelect.addEventListener('showDropdown', (event: any) => {
            const currentSearchTerm = this.choicesInstance.input?.value || '';
            this.performSearch(currentSearchTerm);
        });
    }

    private createCustomChoiceTemplate(template: any, classNames: any, choice: any, selectText: string, groupName?: string): HTMLDivElement {
        // choice è un oggetto ChoiceFull di Choices.js
        // I miei dati da performSearch sono in choice.customProperties
        const templateData = {
            // Dati di base da Choices.js (ChoiceFull interface)
            id: choice.id,
            value: choice.value,
            label: choice.label,
            selectText: selectText,
            group: choice.group,
            disabled: choice.disabled,

            // I miei dati sono in customProperties (da performSearch)
            customProperties: choice.customProperties || {},

            // Dati computati per il template
            typeDisplayName: this.getTypeDisplayName(choice.customProperties?.type_value),
        };
        // Uso templateParseToElement per renderizzare il template
        const customDiv = document.createElement('div');
        templateParseToElement('form-parameters-input-item-search', customDiv, templateData, false, this.nunjucksEnvCallbacks);

        if (!customDiv.hasChildNodes()) {
            // Fallback al template di default di Choices.js se il nostro template non funziona
            console.warn('Template personalizzato non trovato, uso il default di Choices.js');
            return template.choice(classNames, choice, selectText, groupName);
        }


        // Aggiungo le classi CSS richieste da Choices.js
        const { item, itemChoice, itemSelectable, selectedState, itemDisabled, placeholder } = classNames.classNames;

        customDiv.classList.add(...item, ...itemChoice);
        customDiv.id = choice.elementId;

        // Gestione stati Choices.js
        if (choice.selected) {
            customDiv.classList.add(...selectedState);
        }

        if (choice.placeholder) {
            customDiv.classList.add(...placeholder);
        }

        if (choice.disabled) {
            customDiv.classList.add(...itemDisabled);
        } else {
            customDiv.classList.add(...itemSelectable);
        }

        return customDiv;
    }



    get paramTypeMap(): Record<string, string> {
        return PARAM_TYPE_MAP;
    }

    get paramTypeValueMap(): Record<string, string> {
        return PARAM_TYPE_VALUE_MAP;
    }

    private getTypeDisplayName(typeValue: string): string {
        const typeMap = this.paramTypeMap;
        return typeMap[typeValue] || typeValue;
    }
    private getTypeValueDisplayName(typeValue: string): string {
        const typeValueMap = this.paramTypeValueMap;
        return typeValueMap[typeValue] || typeValue;
    }

    private removeCurrentParameter(): void {
        if (!this.current) {
            return;
        }
        showConfirm("Sei sicuro di voler rimuovere questo parametro?", {
        })
            .then((confirmed) => {
                if (!confirmed || !this.current?.id) {
                    return;
                }
                window.api.manager.params.delete(this.current.id)
                    .then((result: ApiResponse<ParamDefRow | null>) => {
                        if (result.success) {
                            this.value = ''; // Reset the value after removal
                            this.choicesInstance?.removeActiveItems(); // Remove the item from Choices.js
                            this.choicesInstance?.clearChoices();
                        } else {
                            console.error('Errore durante la rimozione del parametro:', result.error);
                        }
                    });
            });
    }

    private getFormData(): Record<string, any> {
        // const formData = new FormData(this.attributesForm);
        const data: Record<string, any> = this.parametersForm.getFormData();

        // FormData ignora i campi disabled, li aggiungo manualmente
        const allInputs = this.parametersForm.querySelectorAll('input, select, textarea');
        allInputs.forEach((input: Element) => {
            if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement || input instanceof HTMLTextAreaElement) {
                if (input.name && input.disabled) {
                    data[input.name] = input.value;
                }
            }
        });

        data['attribute_id'] = null;
        // qui abbiamo anche il componente <attributes-input>
        const attributesInput = this.parametersForm.querySelector('attributes-input');
        if (attributesInput && attributesInput instanceof AttributesComponent) {
            data['attribute_id'] = attributesInput.value
        }

        // Gestione parameter-type component
        const parameterTypeInput = this.parametersForm.querySelector('parameter-type') as ParameterTypeComponent | null;
        if (parameterTypeInput && parameterTypeInput.value) {
            const paramValue = parameterTypeInput.value; // {typeName, typeValue}
            data['type_name'] = paramValue.typeName;
            data['type_value'] = paramValue.typeValue;
        }

        data['organization_id'] = data['organization_id'] ? parseInt(data['organization_id'] as string, 10) : 999;

        return data;
    }

    private clearForm(): void {
        // Qui non è necessario resettare il form, perché il form viene ricreato ad ogni cambio di step
    }

    private formSubmitHandler(event: Event): void {
        event.preventDefault();
        const attributeInput: AttributesComponent | null = this.parametersForm.querySelector('attributes-input') || null;
        if (this.parametersForm.checkValidity() === false) {
            console.warn('Form non valido, verifica i campi richiesti');
            this.parametersForm.reportValidity();
            return;
        }
        const data = this.getFormData();
        const action = this.parametersForm.dataset.action || 'add';
        if (action !== 'add' && action !== 'edit') {
            console.error('Azione del form non valida:', action);
            return;
        }

        // Estraggo i dati specifici per i parametri
        const { name, description = "", type_name, type_value, attribute_id } = data;
        const { organization_id } = data;
        const attribute_id_num = attribute_id ? parseInt(attribute_id, 10) : undefined;
        let payload: ParamDefRowNoId | ParamDefRow = {
            organization_id: parseInt(organization_id, 10) || 0,
            name,
            description,
            type_name,
            type_value,
            attribute_id: attribute_id_num,
            parameter_data: {
                name,
                description,
                type_name,
                type_value,
                attribute_id: attribute_id_num
            },
            is_core: parseInt(organization_id, 10) === 0,
            metadata: {},
        }

        try {
            if (action === 'edit') {
                const id = parseInt(data.id, 10);
                if (!isNumber(id)) {
                    console.error('ID non valido per l\'azione di modifica');
                    return;
                }
                (payload as ParamDefRow).id = id;
            }
            const valid = action === 'edit' ? paramDefRowSchema.safeParse(payload) : paramDefRowSchemaNoId.safeParse(payload);
            if (!valid.success) {
                showError('Controllare i valori inseriti: input non valido');
                console.error('Payload non valido:', valid.error);
                return;
            }

            if (attributeInput && attributeInput.current) {
                const attributeTypeValue: AttributeTypeValue = attributeInput.current.attribute_data.type_value;
                const parameterTypeValue: ParamTypeValue = type_value;
                if (!paramAttributeCheckValidity(attributeTypeValue, parameterTypeValue)) {
                    showError(paramAttributeReportValidity(attributeTypeValue, parameterTypeValue));
                    return;
                }
            }
            const method = action === 'add' ? 'add' : 'update';

            window.api.manager.params[method]((payload as any))
                .then((result: ApiResponse<{ id: number }>) => {
                    if (!result.success) {
                        console.log('payload:', payload);
                        console.error(result.error);
                        return;
                    }
                    else {
                        console.log('Azione completata con successo:', result.data);
                        const id = result.data?.id;
                        if (id) {
                            this.addParameterToChoicesById(id);
                        }
                        this.afterSave();
                    }
                });
        } catch (error) {
            console.error('Errore durante il submit del form:', error);
        }
    }
    protected afterSave(): void {
        this.step = this.steps.edit; // Torno alla lista dopo l'aggiunta
    }
    protected valueChanged(newValue: string): void {
        if (this.isValidValue(newValue)) {
            this.dispatch('value-changed', { value: newValue, origin: this }, true, true);
        }
        if (this.editParameterBtn && this.current) {
            this.editParameterBtn.classList.toggle('d-none', !newValue ||
                (this.current.is_core && !this.isAdmin));
        }
        if (this.removeParameterBtn && this.current) {
            this.removeParameterBtn.classList.toggle('d-none', !newValue ||
                (this.current.is_core && !this.isAdmin));
        }
        if (newValue == "") {
            this.editParameterBtn?.classList.add('d-none');
            this.removeParameterBtn?.classList.add('d-none');
        }
    }

    protected afterRender(): void {
        super.afterRender();
        this.panelList = this.roleElementSelector(this.steps.edit) as HTMLDivElement;
        this.listSelect = this.roleElementSelector('parameters-select') as HTMLSelectElement;
        this.removeParameterBtn = this.roleElementSelector('remove-parameter-btn') as HTMLButtonElement;
        this.addParameterBtn = this.roleElementSelector('add-parameter-btn') as HTMLButtonElement;
        this.editParameterBtn = this.roleElementSelector('edit-parameter-btn') as HTMLButtonElement;
        // this.parametersForm = this.roleElementSelector('parameters-form') as FormComponent;
        this.initializeChoices();
        this.removeParameterBtn?.addEventListener('click', (event: MouseEvent) => {
            event.preventDefault();
            this.removeCurrentParameter();
        });

        // Registra l'event handler per il form submit
        this.parametersForm?.addEventListener('submit', this.formSubmitHandler.bind(this));

        // Processa il valore iniziale DOPO aver inizializzato Choices
        this.processParameterValue();
        this.renderProcessView();
    }

    protected buildRenderingData(): Record<string, any> {
        const user = this.user;
        let organization_id = user.organization_id || 0; // Default to 0 if not set
        let organizations: any[] = [];
        const data = {
            organization_id,
            organization_name: user.organization_name || '',
            role: user.role,
            adminNotImpersonating: this.adminNotImpersonating,
            isAdmin: Boolean(this.isAdmin && !this.noDelete),
            isEdit: Boolean(this.isEdit && !this.noEdit),
            hideCloseButton: this.hideCloseButton,
            steps: this.steps,
            is_core: organization_id === 0, // Attributo di sistema se organization_id è 0
        };
        return data;
    }
    protected render(): void {
        const user = this.user;
        if (!user) {
            this.onRenderError('User not found');
            return;
        }
        super.render();
        // this.performSearch('a');
    }
}